import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from '../../lib/api';
import { leadActions, lists as listsApi, type LeadTarget } from '../../lib/resources';
import { targetLabel } from '../../lib/lead-target';
import type { Job } from '../../types';
import { Button, ErrorNote, Field, Modal, inputClass } from '../ui';

/** Adicionar às listas / Remover da lista. */
export function ListsActionModal({
  mode,
  target,
  count,
  onClose,
  onDone,
}: {
  mode: 'add' | 'remove';
  target: LeadTarget;
  count: number | null;
  onClose: () => void;
  onDone: (affected: number) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const lists = useQuery({ queryKey: ['lists'], queryFn: () => listsApi.list() });
  const run = useMutation({
    mutationFn: () =>
      mode === 'add'
        ? leadActions.addToLists(target, selected)
        : leadActions.removeFromLists(target, selected),
    onSuccess: (result) => onDone(result.affected),
  });

  return (
    <Modal
      title={mode === 'add' ? 'Adicionar às listas' : 'Remover da lista'}
      onClose={onClose}
    >
      <div className="space-y-4 text-sm">
        <p className="text-slate-600">
          {mode === 'add' ? 'Adicionar' : 'Remover'}{' '}
          <strong>{targetLabel(target, count)}</strong>{' '}
          {mode === 'add' ? 'às listas escolhidas.' : 'das listas escolhidas.'}
        </p>
        <div className="max-h-60 space-y-1 overflow-y-auto">
          {(lists.data?.data ?? []).map((list) => (
            <label key={list.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selected.includes(list.id)}
                onChange={() =>
                  setSelected((current) =>
                    current.includes(list.id)
                      ? current.filter((id) => id !== list.id)
                      : [...current, list.id],
                  )
                }
              />
              {list.name}
            </label>
          ))}
          {(lists.data?.data ?? []).length === 0 && (
            <p className="text-xs text-slate-400">Nenhuma lista cadastrada.</p>
          )}
        </div>
        {run.error && <ErrorNote>{errorMessage(run.error)}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={selected.length === 0 || run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending ? 'Aplicando…' : 'Confirmar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** "Criar nova lista" já com os leads. */
export function CreateListModal({
  target,
  count,
  onClose,
  onDone,
}: {
  target: LeadTarget;
  count: number | null;
  onClose: () => void;
  onDone: (affected: number) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const run = useMutation({
    mutationFn: () =>
      leadActions.createList(target, name.trim(), description.trim() || undefined),
    onSuccess: (result) => onDone(result.affected),
  });

  return (
    <Modal title="Criar nova lista" onClose={onClose}>
      <form
        className="space-y-4 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          run.mutate();
        }}
      >
        <p className="text-slate-600">
          A lista nasce com <strong>{targetLabel(target, count)}</strong>.
        </p>
        <Field label="Nome *">
          <input
            required
            autoFocus
            className={inputClass}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Descrição">
          <input
            className={inputClass}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        {run.error && <ErrorNote>{errorMessage(run.error)}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={!name.trim() || run.isPending}>
            Confirmar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** "Exportar leads": usa os filtros e a busca atuais; vira execução. */
export function ExportModal({
  filter,
  count,
  onClose,
}: {
  filter: string;
  count: number | null;
  onClose: () => void;
}) {
  const [format, setFormat] = useState<'CSV' | 'JSON'>('CSV');
  const [job, setJob] = useState<Job | null>(null);
  const run = useMutation({
    mutationFn: () => leadActions.export(format, filter),
    onSuccess: setJob,
  });

  return (
    <Modal title="Exportar leads" onClose={onClose}>
      {job ? (
        <div className="space-y-4 text-sm text-slate-600">
          <p>Exportação criada. O arquivo fica pronto em Configurações › Execuções.</p>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Fechar</Button>
            <Link to="/configuracoes/execucoes">
              <Button variant="primary">Ver execuções</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <p className="text-slate-600">
            A exportação será criada usando os <strong>filtros e a busca atuais</strong>
            {count !== null && ` (${count} lead(s))`}.
          </p>
          <div className="flex gap-4">
            {(['CSV', 'JSON'] as const).map((value) => (
              <label key={value} className="flex items-center gap-1.5">
                <input
                  type="radio"
                  checked={format === value}
                  onChange={() => setFormat(value)}
                />
                {value}
              </label>
            ))}
          </div>
          {run.error && <ErrorNote>{errorMessage(run.error)}</ErrorNote>}
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button variant="primary" disabled={run.isPending} onClick={() => run.mutate()}>
              Criar exportação
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
